import {
  Vec2,
  makeConstraint,
  makePolyline,
  type EdgeRef,
  type Entity,
  type PointRef,
} from '@lakaran/geometry';
import { pickConstraintTarget, type PickTarget } from '@lakaran/engine';
import { Tool, type ToolField } from './tool';
import type { CadEditor } from '../editor';

/**
 * Constraint creation tools. Each one turns picks into persistent constraints:
 * vertex snaps bind handles, edge picks bind segments, and free snaps become anchors.
 */
abstract class ConstraintTool extends Tool {
  protected marks: Vec2[] = [];
  protected snap?: string;

  protected abstract readonly introduction: string;

  protected begin(): void {
    this.marks = [];
    this.host.prompt(this.introduction);
  }

  override cancel(): void {
    this.marks = [];
    this.host.setDraft([]);
    this.host.endTool();
  }

  protected hostEditor(): CadEditor {
    return this.host.editor;
  }

  protected target(point: Vec2, prefer: 'point' | 'edge' = 'point'): PickTarget {
    const editor = this.hostEditor();
    const pick = pickConstraintTarget(
      editor.doc,
      point,
      editor.viewport.pxToWorld(editor.settings.snapRadiusPx),
      editor.activeSnap?.type,
      prefer,
    );
    this.snap = pick.snap;
    return pick;
  }

  /** Small square marker so the picked point stays visible. */
  protected mark(point: Vec2): void {
    this.marks.push(point);
    const half = this.hostEditor().viewport.pxToWorld(4);
    this.host.setDraft(
      this.marks.map((center) => marker(center, half, this.host.editor.doc.currentLayerId)),
    );
  }

  protected finish(): void {
    this.marks = [];
    this.host.setDraft([]);
    this.host.endTool();
  }
}

function marker(center: Vec2, half: number, layerId: string): Entity {
  return makePolyline(
    [
      Vec2.of(center.x - half, center.y - half),
      Vec2.of(center.x + half, center.y - half),
      Vec2.of(center.x + half, center.y + half),
      Vec2.of(center.x - half, center.y + half),
    ],
    true,
    layerId,
  );
}

export class CoincidentTool extends ConstraintTool {
  readonly name = 'COINCIDENT';
  readonly title = 'Coincident';
  readonly aliases = ['CO', 'COIN'] as const;
  protected readonly introduction =
    'COINCIDENT  Select the point to move (vertex snap), then the point to meet it.';

  private moving?: PointRef;

  point(p: Vec2): void {
    const pick = this.target(p);

    if (!this.moving) {
      if (pick.kind !== 'handle') {
        this.host.message('COINCIDENT  Snap to a vertex or a centre first.');
        return;
      }
      this.moving = pick.ref;
      this.mark(pick.point);
      this.host.prompt('COINCIDENT  Now pick the target vertex, edge or point.');
      return;
    }

    const moving = this.moving;
    if (
      pick.kind === 'handle' &&
      pick.ref.entityId === moving.entityId &&
      pick.ref.handle === moving.handle
    ) {
      this.host.message('COINCIDENT  Pick a different point.');
      return;
    }

    if (pick.kind === 'handle') {
      this.hostEditor().applyConstraint(
        makeConstraint({
          kind: 'coincident',
          point: moving,
          target: pick.ref,
          source: this.snap === 'endpoint' || this.snap === 'center' ? 'snap' : 'user',
          snap: this.snap,
        }),
      );
    } else if (pick.kind === 'edge') {
      this.hostEditor().applyConstraint(
        makeConstraint({
          kind: 'pointOnEdge',
          point: moving,
          edge: pick.edge,
          source: 'snap',
          snap: this.snap,
        }),
      );
    } else {
      this.hostEditor().applyConstraint(
        makeConstraint({
          kind: 'anchor',
          point: moving,
          at: pick.point,
          source: 'snap',
          snap: this.snap,
        }),
      );
    }
    this.finish();
  }
}

export class FixTool extends ConstraintTool {
  readonly name = 'FIX';
  readonly title = 'Fix point';
  readonly aliases = ['FX'] as const;
  protected readonly introduction = 'FIX  Select a vertex or centre to lock in place.';

  point(p: Vec2): void {
    const pick = this.target(p);
    if (pick.kind !== 'handle') {
      this.host.message('FIX  Snap to a vertex or a centre first.');
      return;
    }
    this.hostEditor().applyConstraint(
      makeConstraint({
        kind: 'fix',
        point: pick.ref,
        source: this.snap === 'endpoint' ? 'snap' : 'user',
        snap: this.snap,
      }),
    );
    this.finish();
  }
}

abstract class EdgeConstraintTool extends ConstraintTool {
  protected firstEdge?: EdgeRef;
  protected abstract readonly single: boolean;

  point(p: Vec2): void {
    const pick = this.target(p, 'edge');
    if (pick.kind !== 'edge') {
      this.host.message(`${this.name}  Select a line or a polyline segment.`);
      return;
    }
    if (this.single) {
      this.apply(pick.edge, undefined);
      return;
    }
    if (!this.firstEdge) {
      this.firstEdge = pick.edge;
      this.mark(pick.point);
      this.host.prompt(`${this.name}  Now pick the second edge.`);
      return;
    }
    this.apply(this.firstEdge, pick.edge);
  }

  protected abstract apply(first: EdgeRef, second?: EdgeRef): void;
}

export class HorizontalTool extends EdgeConstraintTool {
  readonly name = 'HORIZONTAL';
  readonly title = 'Horizontal';
  readonly aliases = ['HOR'] as const;
  protected readonly introduction = 'HORIZONTAL  Select an edge to level.';
  protected readonly single = true;

  protected apply(first: EdgeRef): void {
    this.hostEditor().applyConstraint(
      makeConstraint({ kind: 'horizontal', edge: first, source: 'user' }),
    );
    this.finish();
  }
}

export class VerticalTool extends EdgeConstraintTool {
  readonly name = 'VERTICAL';
  readonly title = 'Vertical';
  readonly aliases = ['VER'] as const;
  protected readonly introduction = 'VERTICAL  Select an edge to plumb.';
  protected readonly single = true;

  protected apply(first: EdgeRef): void {
    this.hostEditor().applyConstraint(makeConstraint({ kind: 'vertical', edge: first, source: 'user' }));
    this.finish();
  }
}

export class ParallelTool extends EdgeConstraintTool {
  readonly name = 'PARALLEL';
  readonly title = 'Parallel';
  readonly aliases = ['PAR'] as const;
  protected readonly introduction = 'PARALLEL  Select the first edge.';
  protected readonly single = false;

  protected apply(first: EdgeRef, second?: EdgeRef): void {
    if (!second) return;
    this.hostEditor().applyConstraint(
      makeConstraint({ kind: 'parallel', edge: first, other: second, source: 'user' }),
    );
    this.finish();
  }
}

export class PerpendicularTool extends EdgeConstraintTool {
  readonly name = 'PERPENDICULAR';
  readonly title = 'Perpendicular';
  readonly aliases = ['PERP'] as const;
  protected readonly introduction = 'PERPENDICULAR  Select the first edge.';
  protected readonly single = false;

  protected apply(first: EdgeRef, second?: EdgeRef): void {
    if (!second) return;
    this.hostEditor().applyConstraint(
      makeConstraint({ kind: 'perpendicular', edge: first, other: second, source: 'user' }),
    );
    this.finish();
  }
}

export class OnEdgeTool extends ConstraintTool {
  readonly name = 'ONEDGE';
  readonly title = 'Point on edge';
  readonly aliases = ['ON'] as const;
  protected readonly introduction = 'ONEDGE  Select a vertex, then the edge it should lie on.';

  private pointRef?: PointRef;

  point(p: Vec2): void {
    if (!this.pointRef) {
      const pick = this.target(p);
      if (pick.kind !== 'handle') {
        this.host.message('ONEDGE  Snap to a vertex or a centre first.');
        return;
      }
      this.pointRef = pick.ref;
      this.mark(pick.point);
      this.host.prompt('ONEDGE  Now pick the edge.');
      return;
    }

    const pick = this.target(p, 'edge');
    if (pick.kind !== 'edge') {
      this.host.message('ONEDGE  Select a line or a polyline segment.');
      return;
    }
    this.hostEditor().applyConstraint(
      makeConstraint({
        kind: 'pointOnEdge',
        point: this.pointRef,
        edge: pick.edge,
        source: this.snap === 'endpoint' ? 'snap' : 'user',
        snap: this.snap,
      }),
    );
    this.finish();
  }
}

export class DistanceTool extends ConstraintTool {
  readonly name = 'DISTANCE';
  readonly title = 'Distance';
  readonly aliases = ['DIST', 'CDIST'] as const;
  protected readonly introduction = 'DISTANCE  Select the first vertex.';

  private first?: PointRef;
  private second?: PointRef;
  private firstPoint?: Vec2;
  private secondPoint?: Vec2;

  point(p: Vec2): void {
    const pick = this.target(p);
    if (pick.kind !== 'handle') {
      this.host.message('DISTANCE  Snap to a vertex or a centre.');
      return;
    }
    if (!this.first) {
      this.first = pick.ref;
      this.firstPoint = pick.point;
      this.mark(pick.point);
      this.host.prompt('DISTANCE  Select the second vertex.');
      return;
    }
    if (pick.ref.entityId === this.first.entityId && pick.ref.handle === this.first.handle) {
      this.host.message('DISTANCE  Pick a different point.');
      return;
    }
    this.second = pick.ref;
    this.secondPoint = pick.point;
    this.mark(pick.point);
    this.host.prompt('DISTANCE  Type the length and press Enter (or Enter to keep it).');
  }

  private measure(): number {
    if (!this.firstPoint || !this.secondPoint) return 0;
    return this.firstPoint.distanceTo(this.secondPoint);
  }

  override getFields(): ToolField[] | null {
    if (!this.first || !this.second) return null;
    return [
      { id: 'length', label: 'Length', unit: 'mm', value: this.measure(), decimals: 3, min: 1e-6 },
    ];
  }

  override applyFields(values: Record<string, number>): void {
    this.commit(values.length ?? this.measure());
  }

  override enter(): void {
    if (this.first && this.second) {
      this.commit(this.measure());
      return;
    }
    this.host.endTool();
  }

  private commit(value: number): void {
    if (!this.first || !this.second || !(value > 0)) {
      this.host.endTool();
      return;
    }
    this.hostEditor().applyConstraint(
      makeConstraint({
        kind: 'distance',
        point: this.first,
        target: this.second,
        value,
        source: 'user',
      }),
    );
    this.finish();
  }
}

export function createConstraintTools(): Array<() => Tool> {
  return [
    () => new CoincidentTool(),
    () => new FixTool(),
    () => new HorizontalTool(),
    () => new VerticalTool(),
    () => new ParallelTool(),
    () => new PerpendicularTool(),
    () => new OnEdgeTool(),
    () => new DistanceTool(),
  ];
}
